/**
 * Add Item Page - Mobile-first flow for adding new rugs
 *
 * Werkbank-render (fase 2): één formulier in plaats van vier stappen, zelfde
 * volgorde als de voorinspectie (soort, vorm, maat, pakket, extra's, notitie),
 * prijs live in de kop van de vaste balk, één Toevoegen. Payload naar
 * createWasserijItem ongewijzigd.
 *
 * Voorheen een 4-stappen-flow:
 * 1. Select Primary Product (rug type)
 * 2. Enter Dimensions (preset options or custom)
 * 3. Select Package (optional cleaning package)
 * 4. Select Extras (optional add-ons)
 *
 * Features:
 * - 24-hour cached product catalog
 * - Live price preview
 * - Online-only (phase 1)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WbButton, WbFactRow, WbFlag, WbInput, WbState, WbStepper, WbTextarea, WbTile, WbToggleGroup, StickyActionBar } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import {
  getCachedProductCatalog,
  cacheProductCatalog,
  getProductCatalogCacheAge,
} from '../lib/db/database';
import type {
  ProductCatalog,
  PricePreviewResponse,
  PresetDimension,
  PrimaryProduct,
  PackageProduct,
  ExtraProduct,
  PriceLine,
} from '../lib/salesforce';

// Preset dimension options as specified
const PRESET_DIMENSIONS: PresetDimension[] = [
  { label: '230 × 160 cm', lengthCm: 230, widthCm: 160 },
  { label: '300 × 200 cm', lengthCm: 300, widthCm: 200 },
  { label: '400 × 300 cm', lengthCm: 400, widthCm: 300 },
];

// Preset diameter options for round rugs
const PRESET_DIAMETERS = [
  { label: '150 cm', diameterCm: 150 },
  { label: '200 cm', diameterCm: 200 },
  { label: '250 cm', diameterCm: 250 },
];

// Shape options
type Shape = 'rechthoekig' | 'rond' | 'anders';

export function AddItemPage() {
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  // Form state
  const [primaryProductId, setPrimaryProductId] = useState<string | undefined>();
  const [shape, setShape] = useState<Shape>('rechthoekig');
  const [lengthCm, setLengthCm] = useState<number | undefined>();
  const [widthCm, setWidthCm] = useState<number | undefined>();
  const [diameterCm, setDiameterCm] = useState<number | undefined>();
  const [surfaceM2, setSurfaceM2] = useState<number | undefined>();
  const [quantity, setQuantity] = useState(1);
  const [packageId, setPackageId] = useState<string | undefined>();
  const [selectedExtraIds, setSelectedExtraIds] = useState<string[]>([]);
  const [driverNotes, setDriverNotes] = useState('');
  const [useCustomDimensions, setUseCustomDimensions] = useState(false);

  // Data state
  const [catalog, setCatalog] = useState<ProductCatalog | null>(null);
  const [pricePreview, setPricePreview] = useState<PricePreviewResponse | null>(null);
  const [cacheAge, setCacheAge] = useState<number | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load product catalog (cached or fresh)
  useEffect(() => {
    async function loadCatalog() {
      if (!workOrderId) {
        setError('Geen werkorder ID gevonden');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // For online-only phase 1, always fetch fresh with werkOrderId for regiotoeslag
        // TODO: Implement smarter caching that separates base catalog from regiotoeslag
        const response = await salesforceClient.getProductCatalog(workOrderId);
        if (response.success && response.data) {
          setCatalog(response.data);
          // Auto-select first package as default (sorted by Policy_Priority__c)
          if (response.data.packages && response.data.packages.length > 0) {
            setPackageId(response.data.packages[0]!.productId);
          }
          // Cache for offline reference (but regiotoeslag may be outdated)
          await cacheProductCatalog(response.data);
          setCacheAge(0);
        } else {
          // Fallback to cached catalog if online fetch fails
          const cached = await getCachedProductCatalog();
          if (cached) {
            setCatalog(cached);
            // Auto-select first package as default
            if (cached.packages && cached.packages.length > 0) {
              setPackageId(cached.packages[0]!.productId);
            }
            const age = await getProductCatalogCacheAge();
            setCacheAge(age);
          } else {
            setError(response.message || 'Kon productcatalogus niet laden');
          }
        }
      } catch (err) {
        console.error('Failed to load catalog:', err);
        // Try fallback to cache
        const cached = await getCachedProductCatalog();
        if (cached) {
          setCatalog(cached);
          // Auto-select first package as default
          if (cached.packages && cached.packages.length > 0) {
            setPackageId(cached.packages[0]!.productId);
          }
          const age = await getProductCatalogCacheAge();
          setCacheAge(age);
        } else {
          setError('Kon productcatalogus niet laden. Controleer je internetverbinding.');
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadCatalog();
  }, [workOrderId]);

  // Calculate price preview when relevant fields change
  useEffect(() => {
    async function calculatePrice() {
      // Check if we have valid dimensions based on shape
      const hasValidDimensions =
        (shape === 'rechthoekig' && lengthCm && widthCm) ||
        (shape === 'rond' && diameterCm) ||
        (shape === 'anders' && surfaceM2);

      if (!workOrderId || !primaryProductId || !hasValidDimensions) {
        setPricePreview(null);
        return;
      }

      // Calculate effective dimensions for API
      const effectiveLengthCm = lengthCm;
      const effectiveWidthCm = widthCm;
      let effectiveSurfaceM2 = surfaceM2;

      if (shape === 'rond' && diameterCm) {
        // For round rugs, calculate surface from diameter
        effectiveSurfaceM2 = Math.PI * Math.pow(diameterCm / 200, 2); // π * r² in m²
      } else if (shape === 'rechthoekig' && lengthCm && widthCm) {
        effectiveSurfaceM2 = (lengthCm * widthCm) / 10000;
      }

      setIsCalculating(true);
      try {
        const response = await salesforceClient.calculatePricePreview({
          werkOrderId: workOrderId,
          primaryProductId,
          packageId,
          lengthCm: effectiveLengthCm || 0,
          widthCm: effectiveWidthCm || 0,
          diameterCm: shape === 'rond' ? diameterCm : undefined,
          surfaceM2: shape === 'anders' ? surfaceM2 : undefined,
          quantity,
          selectedExtraIds,
        });

        if (response.success && response.data) {
          // Override m2 with our calculated value for display
          if (effectiveSurfaceM2) {
            response.data.m2 = effectiveSurfaceM2;
          }
          setPricePreview(response.data);
        }
      } catch (err) {
        console.error('Price calculation failed:', err);
        // Don't show error for price preview - it's not critical
      } finally {
        setIsCalculating(false);
      }
    }

    // Debounce price calculation
    const timer = setTimeout(calculatePrice, 300);
    return () => clearTimeout(timer);
  }, [workOrderId, primaryProductId, packageId, lengthCm, widthCm, diameterCm, surfaceM2, shape, quantity, selectedExtraIds]);

  // Handle form submission
  const handleSubmit = async () => {
    // Validate based on shape
    const hasValidDimensions =
      (shape === 'rechthoekig' && lengthCm && widthCm) ||
      (shape === 'rond' && diameterCm) ||
      (shape === 'anders' && surfaceM2);

    if (!workOrderId || !primaryProductId || !hasValidDimensions) {
      setError('Vul alle verplichte velden in');
      return;
    }

    // Calculate effective values for API
    const effectiveLengthCm = lengthCm;
    const effectiveWidthCm = widthCm;
    let effectiveDiameterCm = diameterCm;
    let effectiveSurfaceM2 = surfaceM2;

    if (shape === 'rond' && diameterCm) {
      effectiveSurfaceM2 = Math.PI * Math.pow(diameterCm / 200, 2);
      effectiveDiameterCm = diameterCm;
    } else if (shape === 'rechthoekig' && lengthCm && widthCm) {
      effectiveSurfaceM2 = (lengthCm * widthCm) / 10000;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await salesforceClient.createWasserijItem(workOrderId, {
        primaryProductId,
        packageId,
        lengthCm: effectiveLengthCm,
        widthCm: effectiveWidthCm,
        diameterCm: effectiveDiameterCm,
        surfaceM2: effectiveSurfaceM2,
        quantity,
        selectedExtraIds,
        driverNotes: driverNotes || undefined,
      });

      if (response.success) {
        // Navigate back to route detail page with state to expand this work order
        navigate(-1 as unknown as string, { state: { expandWorkOrderId: workOrderId } });
      } else {
        setError(response.message || 'Kon item niet toevoegen');
      }
    } catch (err) {
      console.error('Failed to create item:', err);
      setError('Kon item niet toevoegen. Probeer opnieuw.');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle extra selection
  const toggleExtra = (extraId: string) => {
    setSelectedExtraIds((prev) =>
      prev.includes(extraId)
        ? prev.filter((id) => id !== extraId)
        : [...prev, extraId]
    );
  };

  // Select preset dimension
  const selectPreset = (preset: PresetDimension) => {
    setShape('rechthoekig');
    setLengthCm(preset.lengthCm);
    setWidthCm(preset.widthCm);
    setDiameterCm(undefined);
    setSurfaceM2(undefined);
    setUseCustomDimensions(false);
  };

  // Select preset diameter for round rugs
  const selectDiameterPreset = (preset: { label: string; diameterCm: number }) => {
    setDiameterCm(preset.diameterCm);
    setUseCustomDimensions(false);
  };

  const hasValidDimensions =
    (shape === 'rechthoekig' && !!lengthCm && !!widthCm && lengthCm > 0 && widthCm > 0) ||
    (shape === 'rond' && !!diameterCm && diameterCm > 0) ||
    (shape === 'anders' && !!surfaceM2 && surfaceM2 > 0);
  const canSubmit = !!primaryProductId && hasValidDimensions;

  // Get selected primary product details
  const selectedPrimary = catalog?.primaryProducts.find((p: PrimaryProduct) => p.productId === primaryProductId);

  useHeader('Kleed toevoegen', pricePreview && !isCalculating ? `€${pricePreview.totalInclBtw.toFixed(2).replace('.', ',')}` : null);

  if (isLoading) {
    return <WbState variant="laden" titel="Productcatalogus laden…" className="text-center min-h-[60vh] flex items-center justify-center" />;
  }

  if (error && !catalog) {
    return (
      <div className="space-y-3">
        <WbButton variant="link" onClick={() => navigate(-1)}>‹ Terug</WbButton>
        <WbState variant="fout" titel={error} onOpnieuw={() => window.location.reload()} />
      </div>
    );
  }

  const m2 = shape === 'rechthoekig' && lengthCm && widthCm
    ? (lengthCm * widthCm) / 10000
    : shape === 'rond' && diameterCm
      ? Math.PI * Math.pow(diameterCm / 200, 2)
      : surfaceM2 ?? null;

  return (
    <div className="space-y-5 pb-[180px]">
      <WbButton variant="link" onClick={() => navigate(-1)}>‹ Annuleren</WbButton>
      {cacheAge !== null && cacheAge > 0 && <WbFlag kind="opmerking">Prijzen van {cacheAge} uur geleden (offline catalogus).</WbFlag>}

      {/* Soort: één keuze */}
      {catalog && (
        <Sectie titel="Soort vloerkleed">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {catalog.primaryProducts.map((product: PrimaryProduct) => {
              // Prijs incl. btw (21%) en regiotoeslag, als indicatie
              const basePrice = product.basePrice || 0;
              const regioToeslag = catalog.regioToeslagPercentage || 0;
              const priceInclBtw = basePrice * (1 + regioToeslag / 100) * 1.21;
              return (
                <WbTile key={product.productId} rol="radio" selected={primaryProductId === product.productId} onToggle={() => setPrimaryProductId(product.productId)}>
                  <span className="block">{product.productName}</span>
                  <span className="block text-sm font-normal text-wb-inkt2">
                    {product.description ? `${product.description} · ` : ''}
                    {basePrice > 0 ? <span className="font-mono">vanaf €{priceInclBtw.toFixed(2).replace('.', ',')}/m²</span> : null}
                  </span>
                </WbTile>
              );
            })}
          </div>
        </Sectie>
      )}

      {/* Vorm + maat */}
      <Sectie titel="Vorm">
        <WbToggleGroup<Shape>
          label="Vorm"
          options={[
            { value: 'rechthoekig', label: 'Rechthoek' },
            { value: 'rond', label: 'Rond' },
            { value: 'anders', label: 'Anders' },
          ]}
          value={shape}
          onChange={(v) => {
            setShape(v);
            setUseCustomDimensions(false);
            if (v !== 'rechthoekig') { setLengthCm(undefined); setWidthCm(undefined); }
            if (v !== 'rond') setDiameterCm(undefined);
            if (v !== 'anders') setSurfaceM2(undefined);
          }}
        />
      </Sectie>

      <Sectie titel="Maat">
        {shape === 'rechthoekig' && (
          <div className="grid grid-cols-3 gap-1.5">
            {PRESET_DIMENSIONS.map((preset) => (
              <WbTile
                key={preset.label}
                rol="radio"
                selected={!useCustomDimensions && lengthCm === preset.lengthCm && widthCm === preset.widthCm}
                onToggle={() => selectPreset(preset)}
              >
                <span className="block font-mono">{preset.lengthCm} × {preset.widthCm}</span>
                <span className="block text-sm font-normal text-wb-inkt2 font-mono">{((preset.lengthCm * preset.widthCm) / 10000).toFixed(1)} m²</span>
              </WbTile>
            ))}
            <WbTile rol="radio" selected={useCustomDimensions} onToggle={() => { setUseCustomDimensions(true); setLengthCm(undefined); setWidthCm(undefined); }} wide>
              Andere maat
            </WbTile>
          </div>
        )}
        {shape === 'rond' && (
          <div className="grid grid-cols-3 gap-1.5">
            {PRESET_DIAMETERS.map((preset) => (
              <WbTile key={preset.label} rol="radio" selected={!useCustomDimensions && diameterCm === preset.diameterCm} onToggle={() => selectDiameterPreset(preset)}>
                <span className="block font-mono">Ø {preset.diameterCm}</span>
                <span className="block text-sm font-normal text-wb-inkt2 font-mono">{(Math.PI * Math.pow(preset.diameterCm / 200, 2)).toFixed(1)} m²</span>
              </WbTile>
            ))}
            <WbTile rol="radio" selected={useCustomDimensions} onToggle={() => { setUseCustomDimensions(true); setDiameterCm(undefined); }} wide>
              Andere diameter
            </WbTile>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {shape === 'rechthoekig' && useCustomDimensions && (
            <>
              <WbInput label="Lengte" eenheid="cm" mono type="number" inputMode="numeric" min={1} value={lengthCm || ''} onChange={(e) => setLengthCm(e.target.value ? parseInt(e.target.value) : undefined)} placeholder="300" />
              <WbInput label="Breedte" eenheid="cm" mono type="number" inputMode="numeric" min={1} value={widthCm || ''} onChange={(e) => setWidthCm(e.target.value ? parseInt(e.target.value) : undefined)} placeholder="200" />
            </>
          )}
          {shape === 'rond' && useCustomDimensions && (
            <WbInput label="Diameter" eenheid="cm" mono type="number" inputMode="numeric" min={1} value={diameterCm || ''} onChange={(e) => setDiameterCm(e.target.value ? parseInt(e.target.value) : undefined)} placeholder="200" wrapperClassName="col-span-2" />
          )}
          {shape === 'anders' && (
            <WbInput label="Oppervlakte" eenheid="m²" mono type="number" inputMode="decimal" step="0.1" min={0.1} value={surfaceM2 || ''} onChange={(e) => setSurfaceM2(e.target.value ? parseFloat(e.target.value) : undefined)} placeholder="6.5" wrapperClassName="col-span-2" />
          )}
          <div className="min-h-tap bg-wb-inkt text-white rounded-wb px-3 py-1.5 flex flex-col justify-center">
            <span className="text-[11px] font-bold uppercase tracking-wide text-wb-kopink2 leading-tight">m²{quantity > 1 ? ` × ${quantity}` : ''}</span>
            <span className="font-mono text-xl font-semibold">{m2 ? (m2 * quantity).toFixed(2) : '–'}</span>
          </div>
        </div>
        {shape === 'anders' && <p className="text-sm text-wb-inkt2">Schat de totale oppervlakte van het vloerkleed.</p>}
      </Sectie>

      <Sectie titel="Aantal">
        <WbStepper value={quantity} onChange={setQuantity} min={1} label="Aantal" />
      </Sectie>

      {/* Pakket: één keuze, eerste is standaard */}
      {catalog && catalog.packages.length > 0 && (
        <Sectie titel="Wasbehandeling">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {catalog.packages.map((pkg: PackageProduct) => (
              <WbTile key={pkg.productId} rol="radio" selected={packageId === pkg.productId} onToggle={() => setPackageId(pkg.productId)}>
                <span className="block">{pkg.productName}</span>
                {(pkg.description || (pkg.percentage != null && pkg.percentage > 0)) && (
                  <span className="block text-sm font-normal text-wb-inkt2">
                    {pkg.description}{pkg.description && pkg.percentage != null && pkg.percentage > 0 ? ' · ' : ''}
                    {pkg.percentage != null && pkg.percentage > 0 ? <span className="font-mono">+{pkg.percentage}%</span> : null}
                  </span>
                )}
              </WbTile>
            ))}
          </div>
        </Sectie>
      )}

      {/* Extra's: meerkeuze */}
      {catalog && (
        <Sectie titel="Extra's">
          {catalog.extras.length === 0 ? (
            <p className="text-sm text-wb-inkt2">Geen extra opties beschikbaar.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {catalog.extras.map((extra: ExtraProduct) => (
                <WbTile key={extra.productId} selected={selectedExtraIds.includes(extra.productId)} onToggle={() => toggleExtra(extra.productId)}>
                  <span className="block">{extra.productName}</span>
                  <span className="block text-sm font-normal text-wb-inkt2">
                    {extra.description ? `${extra.description} · ` : ''}
                    <span className="font-mono">{extra.pricingType === 'percentage' ? `+${extra.percentage}%` : `€${extra.absolutePrice?.toFixed(2).replace('.', ',')}`}</span>
                  </span>
                </WbTile>
              ))}
            </div>
          )}
        </Sectie>
      )}

      <Sectie titel="Opmerking chauffeur">
        <WbTextarea value={driverNotes} onChange={(e) => setDriverNotes(e.target.value)} placeholder="Optioneel: bijv. klant wil dit kleed apart terug" rows={2} />
      </Sectie>

      {error && <WbFlag kind="geld">{error}</WbFlag>}

      {/* Prijs live + één Toevoegen, vast onderin */}
      {pricePreview && (
        <WbFactRow
          facts={[
            { label: 'Kleed', value: selectedPrimary?.productName ?? '–', tekst: true },
            { label: 'm²', value: `${pricePreview.m2.toFixed(2)}${quantity > 1 ? ` × ${quantity}` : ''}` },
            { label: 'Totaal incl. btw', value: isCalculating ? '…' : `€${pricePreview.totalInclBtw.toFixed(2).replace('.', ',')}` },
          ]}
        />
      )}
      {pricePreview && pricePreview.lines.length > 1 && (
        <div className="-mt-3 space-y-0.5">
          {pricePreview.lines.map((line: PriceLine, idx: number) => (
            <p key={idx} className="flex justify-between text-sm text-wb-inkt2"><span>{line.productName}</span><span className="font-mono">€{line.totalPrice.toFixed(2).replace('.', ',')}</span></p>
          ))}
        </div>
      )}

      <StickyActionBar>
        <WbButton size="lg" onClick={handleSubmit} disabled={!canSubmit} isLoading={isSaving}>
          {canSubmit
            ? `Toevoegen${pricePreview && !isCalculating ? ` · €${pricePreview.totalInclBtw.toFixed(2).replace('.', ',')}` : ''}`
            : !primaryProductId ? 'Kies een soort' : 'Vul de maat in'}
        </WbButton>
      </StickyActionBar>
    </div>
  );
}

function Sectie({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">{titel}</h2>
      {children}
    </section>
  );
}
